import { useMemo } from 'react';
import type { PackEntry } from 'types/shared';
import type { HypothesisCreativeEntry } from '../types/hypothesis';

export const NO_HYPOTHESIS_KEY = 'No hypothesis';

export interface HypothesisPack {
  packIndex: number;
  tag: string;
  creatives: HypothesisCreativeEntry[];
}

type HypothesisKey = string;

interface UseCreativesByPackReturn {
  packsByHypothesis: Map<HypothesisKey, HypothesisPack[]>;
  unpackedCreatives: HypothesisCreativeEntry[];
  packedIds: Set<string>;
}

const hypothesisKey = (
  creative: Pick<HypothesisCreativeEntry, 'hypothesis'>,
): HypothesisKey => creative.hypothesis ?? NO_HYPOTHESIS_KEY;

const useCreativesByPack = (
  packsWithCreatives: PackEntry[],
  packedCreatives: HypothesisCreativeEntry[],
  displayCreatives: HypothesisCreativeEntry[],
): UseCreativesByPackReturn => {
  return useMemo(() => {
    const resolveById = new Map<string, HypothesisCreativeEntry>();
    for (const c of packedCreatives) {
      resolveById.set(c.id, c);
    }
    for (const c of displayCreatives) {
      if (!resolveById.has(c.id)) {
        resolveById.set(c.id, c);
      }
    }

    const packsByHypothesis = new Map<HypothesisKey, HypothesisPack[]>();
    const packedIds = new Set<string>();

    packsWithCreatives.forEach((pack, packIndex) => {
      const byKey = new Map<HypothesisKey, HypothesisCreativeEntry[]>();
      for (const id of pack.creatives) {
        if (typeof id !== 'string') continue;
        packedIds.add(id);
        const creative = resolveById.get(id);
        if (!creative) continue;
        const key = hypothesisKey(creative);
        const bucket = byKey.get(key);
        if (bucket) bucket.push(creative);
        else byKey.set(key, [creative]);
      }
      for (const [key, creatives] of byKey) {
        const list = packsByHypothesis.get(key);
        const entry: HypothesisPack = {
          packIndex,
          tag: pack.tag,
          creatives,
        };
        if (list) list.push(entry);
        else packsByHypothesis.set(key, [entry]);
      }
    });

    const unpackedCreatives = packedIds.size === 0
      ? displayCreatives
      : displayCreatives.filter((c) => !packedIds.has(c.id));

    return { packsByHypothesis, unpackedCreatives, packedIds };
  }, [packsWithCreatives, packedCreatives, displayCreatives]);
};

export default useCreativesByPack;
